// =====================================================================
//  CRONOGRAMA LEGAL
//
//  Los hitos del procedimiento de desamparo (arts. 146 a 152 del Código de
//  Minería) aplicados a la causa C-1856-2026.
//
//  El prototipo los traía escritos en el código. Están en la tabla `hito`
//  porque varios van a cambiar: la fecha efectiva de publicación en el
//  Diario Oficial no se sabe todavía, y entre agosto y septiembre pueden
//  aparecer incidencias procesales.
// =====================================================================

import { useCausa, useHitos } from '../datos/consultas'
import { Alerta, Cargando, ErrorCarga } from '../componentes/ui'
import { Pagina } from '../App'
import { comoFecha, diasHasta, fecha } from '../dominio/formato'
import { AgregarHito, ControlesHito } from './CronogramaEditar'
import type { RolUsuario } from '../tipos/dominio'

export function Cronograma({ rol }: { rol: RolUsuario }) {
  const esMaster = rol === 'MASTER'
  const hitos = useHitos()
  const causa = useCausa()

  if (hitos.isError) return <ErrorCarga error={hitos.error} />
  if (hitos.isLoading || !hitos.data) return <Cargando />

  const hoy = Date.now()
  const c = causa.data

  return (
    <>
      <Pagina
        titulo="Cronograma legal"
        sub={
          <>
            Hitos del procedimiento de desamparo por no pago de patente minera (arts. 146 a 152 del
            Código de Minería) aplicados a la causa {c?.rol ?? 'C-1856-2026'}.
          </>
        }
      />

      {!c?.fecha_publicacion ? (
        <Alerta tono="warn">
          <b>La publicación en el Diario Oficial todavía no está registrada.</b> Es el hito que hay que
          vigilar: sin ella el remate no puede realizarse, y es la que fija los lotes definitivos. El
          límite legal es el 20 de septiembre — 30 días corridos antes del remate, sin contar el día de
          publicación ni el del remate (art. 147).
        </Alerta>
      ) : (
        <Alerta tono="ok">
          Publicada en el Diario Oficial el <b>{fecha(c.fecha_publicacion)}</b>. Con eso quedan
          fijados los lotes definitivos: revisa que la agrupación del portal coincida.
        </Alerta>
      )}

      <div className="card">
        <div className="timeline">
          {hitos.data.map((h) => {
            // comoFecha() ancla las columnas `date` al mediodía local y acepta
            // las dos serializaciones. Concatenar 'T12:00:00' rompía con la
            // forma que entrega el protocolo nativo de Postgres.
            const f = comoFecha(h.fecha)
            const dias = diasHasta(h.fecha)
            // «Cumplido» si ya pasó; «próximo» si falta menos de 45 días, que
            // es la ventana en la que hay que estar mirando.
            const clase = !f ? '' : f.getTime() < hoy ? 'done' : dias < 45 ? 'now' : ''
            return (
              <div className={`tl ${clase}`} key={h.id}>
                <b>{h.titulo}</b>
                <div className="d">
                  {fecha(h.fecha)}
                  {h.articulo ? ` · ${h.articulo}` : ''}
                  {clase === 'done' ? ' · cumplido' : dias > 0 ? ` · en ${dias} días` : ''}
                </div>
                {h.detalle ? <p>{h.detalle}</p> : null}
                {esMaster ? <ControlesHito hito={h} /> : null}
              </div>
            )
          })}
        </div>
      </div>

      {esMaster && c ? <AgregarHito causaId={c.id} /> : null}

      <Alerta tono="info" estilo={{ marginTop: 14 }}>
        <b>El hito que no está en esta lista</b> es el ítem 10 del due diligence: verificar que cada
        concesión que interesa <b>siga en el listado</b> 48 horas antes del remate. Cualquier titular
        puede retirar la suya pagando el doble hasta el momento mismo del remate (art. 149 inc. 2), y
        eso no se avisa.
      </Alerta>
    </>
  )
}
